import {DefaultTheme, useTheme} from 'styled-components';

import {
  ChartEncodingLegendIconType,
  ChartEncodingLegendItem,
} from '../types/ChartEncodingLegendItem';
import {
  getScatterDoughnutRingRadius,
  getScatterDoughnutStrokeWidth,
} from '../utils/getScatterDoughnutStrokeWidth';
import {
  getScatterTrianglePoints,
  getScatterTriangleRingPath,
} from '../utils/getScatterTrianglePoints';

const LEGEND_ICON_SIZE = 12;
const LEGEND_SHAPE_SIZE = 6;
const LEGEND_VIEWBOX_SIZE = 24;
const LEGEND_SHAPE_CENTER = LEGEND_VIEWBOX_SIZE / 2;

interface ChartEncodingLegendIconProps {
  item: ChartEncodingLegendItem;
}

export function ChartEncodingLegendIcon(props: ChartEncodingLegendIconProps) {
  const theme = useTheme();
  const fillColor = theme.colors.palettes.orange['60']['100'];

  return (
    <svg
      width={LEGEND_ICON_SIZE}
      height={LEGEND_ICON_SIZE}
      viewBox={`0 0 ${LEGEND_VIEWBOX_SIZE} ${LEGEND_VIEWBOX_SIZE}`}
      aria-hidden
    >
      {renderIcon(props.item.icon, fillColor, theme)}
    </svg>
  );
}

function renderIcon(icon: ChartEncodingLegendIconType, fillColor: string, theme: DefaultTheme) {
  switch (icon) {
    case 'circle':
      return (
        <circle
          cx={LEGEND_SHAPE_CENTER}
          cy={LEGEND_SHAPE_CENTER}
          r={LEGEND_SHAPE_SIZE}
          fill={fillColor}
        />
      );
    case 'doughnut':
      return (
        <circle
          cx={LEGEND_SHAPE_CENTER}
          cy={LEGEND_SHAPE_CENTER}
          r={getScatterDoughnutRingRadius(LEGEND_SHAPE_SIZE)}
          fill="none"
          stroke={fillColor}
          strokeWidth={getScatterDoughnutStrokeWidth(LEGEND_SHAPE_SIZE)}
        />
      );
    case 'triangle-increased':
      return (
        <polygon
          points={getScatterTrianglePoints(
            LEGEND_SHAPE_CENTER,
            LEGEND_SHAPE_CENTER,
            'increased',
            LEGEND_SHAPE_SIZE
          )}
          fill={fillColor}
        />
      );
    case 'triangle-decreased':
      return (
        <polygon
          points={getScatterTrianglePoints(
            LEGEND_SHAPE_CENTER,
            LEGEND_SHAPE_CENTER,
            'decreased',
            LEGEND_SHAPE_SIZE
          )}
          fill={fillColor}
        />
      );
    case 'triangle-ring-increased':
      return (
        <path
          d={getScatterTriangleRingPath(
            LEGEND_SHAPE_CENTER,
            LEGEND_SHAPE_CENTER,
            'increased',
            LEGEND_SHAPE_SIZE
          )}
          fill={fillColor}
          fillRule="evenodd"
        />
      );
    case 'triangle-ring-decreased':
      return (
        <path
          d={getScatterTriangleRingPath(
            LEGEND_SHAPE_CENTER,
            LEGEND_SHAPE_CENTER,
            'decreased',
            LEGEND_SHAPE_SIZE
          )}
          fill={fillColor}
          fillRule="evenodd"
        />
      );
    case 'border-new':
      return renderBorderIcon(fillColor, theme.colors.palettes.green['60']['100']);
    case 'border-suspicious':
      return renderBorderIcon(fillColor, theme.colors.severity.danger);
    case 'border-dealer':
      return renderBorderIcon(fillColor, theme.colors.palettes.blue['60']['100']);
  }
}

function renderBorderIcon(fillColor: string, strokeColor: string) {
  return (
    <circle
      cx={LEGEND_SHAPE_CENTER}
      cy={LEGEND_SHAPE_CENTER}
      r={LEGEND_SHAPE_SIZE}
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth={2}
    />
  );
}
